"use client";

import { useTranslations } from "next-intl";
import * as React from "react";

import {
  DAY_MS,
  parseIsoDate,
  repaymentAtDay,
  toIsoDate,
} from "@/lib/pricing-calculator/model";
import type { SimulationResult } from "@/lib/pricing-calculator/model";
import { cn } from "@/lib/utils";

import { useCalculatorEnvironment } from "./context";
import { Card } from "./controls";
import type { FrameConnectors } from "./controls";

interface BalanceChartProps {
  primary: SimulationResult;
  /** Uncapped twin, shown as a dashed comparison when a cap is active. */
  comparison: SimulationResult | null;
  startDate: string;
  className?: string;
  stack?: boolean;
  connectors?: FrameConnectors;
}

const HEIGHT = 280;
const HEIGHT_COMPACT = 220;
const PAD = { top: 20, right: 20, bottom: 36, left: 60 } as const;
const MAX_VISIBLE_MONTHS = 36;
const TOOLTIP_GAP = 12;
const TOOLTIP_MIN_WIDTH = 180;

function useElementWidth<T extends HTMLElement>() {
  const ref = React.useRef<T>(null);
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    setWidth(node.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

function stepPath(
  rows: readonly { day: number; balance: number }[],
  total: number,
  x: (day: number) => number,
  y: (value: number) => number,
  maxDay: number
): string {
  let d = `M${x(0)},${y(total)}`;
  let lastX = x(0);
  for (const row of rows) {
    if (row.day > maxDay) break;
    const nx = x(row.day);
    d += `H${nx}V${y(row.balance)}`;
    lastX = nx;
  }
  const last = rows.at(-1);
  if (last && last.day <= maxDay && last.balance > 0) {
    // Forecast ran out before payoff: hold the balance to the edge.
    d += `H${x(maxDay)}`;
    lastX = x(maxDay);
  }
  return `${d}${lastX === x(0) ? "" : ""}`;
}

function monthTickStep(months: number): number {
  if (months <= 8) return 1;
  if (months <= 16) return 2;
  if (months <= 24) return 3;
  return 6;
}

export function BalanceChart({
  primary,
  comparison,
  startDate,
  className,
  stack = false,
  connectors = "none",
}: BalanceChartProps) {
  const t = useTranslations("pages.pricing.calculator.chart");
  const { format } = useCalculatorEnvironment();
  const [containerRef, width] = useElementWidth<HTMLDivElement>();
  const [inspectDay, setInspectDay] = React.useState<number | null>(null);
  const [pinned, setPinned] = React.useState(false);
  const gradientId = React.useId();
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [tooltipWidth, setTooltipWidth] = React.useState(TOOLTIP_MIN_WIDTH);

  const compact = width > 0 && width < 480;
  const height = compact ? HEIGHT_COMPACT : HEIGHT;
  const pad = compact ? { ...PAD, left: 48, right: 12 } : PAD;

  // Domain: the longer of the two schedules, rounded up to a whole
  // funding-month, capped so an unfinished 120-month forecast stays legible.
  const start = React.useMemo(() => parseIsoDate(startDate), [startDate]);
  const monthBounds = primary.months;
  const lastDayNeeded = Math.max(
    primary.rows.at(-1)?.day ?? 0,
    comparison?.rows.at(-1)?.day ?? 0,
    1
  );
  const monthsNeeded = Math.min(
    MAX_VISIBLE_MONTHS,
    Math.max(
      1,
      monthBounds.findIndex(
        (month) =>
          (parseIsoDate(month.end).getTime() - start.getTime()) / DAY_MS >=
          lastDayNeeded
      ) + 1 || monthBounds.length
    )
  );
  const domainEnd = monthBounds[monthsNeeded - 1]?.end;
  const maxDay = domainEnd
    ? Math.round((parseIsoDate(domainEnd).getTime() - start.getTime()) / DAY_MS)
    : lastDayNeeded;

  const plotWidth = Math.max(0, width - pad.left - pad.right);
  const plotHeight = height - pad.top - pad.bottom;
  const x = (day: number) => pad.left + (day / maxDay) * plotWidth;
  const y = (value: number) =>
    pad.top + (1 - value / primary.total) * plotHeight;

  const primaryPath = stepPath(primary.rows, primary.total, x, y, maxDay);
  const areaPath = `${primaryPath}V${y(0)}H${x(0)}Z`;
  const comparisonPath = comparison
    ? stepPath(comparison.rows, comparison.total, x, y, maxDay)
    : null;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(
    (fraction) => fraction * primary.total
  );
  const tickStep = monthTickStep(monthsNeeded);
  const xTicks = monthBounds
    .slice(0, monthsNeeded)
    .map((month, index) => ({
      index: index + 1,
      day: Math.round(
        (parseIsoDate(month.end).getTime() - start.getTime()) / DAY_MS
      ),
    }))
    .filter((tick) => tick.index % tickStep === 0 && tick.day <= maxDay);

  // `rect` is the overlay's own box, which already starts at the plot's left
  // edge, so the pointer offset is measured from it directly.
  const dayFromClientX = (clientX: number, rect: DOMRect) => {
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    return Math.round(Math.min(1, Math.max(0, ratio)) * maxDay);
  };

  const onPointerMove = (event: React.PointerEvent<SVGRectElement>) => {
    if (pinned || event.pointerType === "touch") return;
    setInspectDay(
      dayFromClientX(event.clientX, event.currentTarget.getBoundingClientRect())
    );
  };
  const onPointerDown = (event: React.PointerEvent<SVGRectElement>) => {
    const day = dayFromClientX(
      event.clientX,
      event.currentTarget.getBoundingClientRect()
    );
    setInspectDay(day);
    setPinned(
      event.pointerType === "touch" ? true : !pinned || inspectDay !== day
    );
  };
  const onPointerLeave = () => {
    if (!pinned) setInspectDay(null);
  };
  const onKeyDown = (event: React.KeyboardEvent<SVGRectElement>) => {
    const current = inspectDay ?? 0;
    const shift = event.shiftKey ? 7 : 1;
    let next: number | null = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        next = Math.min(maxDay, current + shift);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        next = Math.max(0, current - shift);
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = maxDay;
        break;
      case "Escape":
        setPinned(false);
        setInspectDay(null);
        return;
      default:
        return;
    }
    event.preventDefault();
    setPinned(true);
    setInspectDay(next);
  };

  const inspect = (() => {
    if (inspectDay === null) return null;
    const primaryAt = repaymentAtDay(primary, inspectDay);
    const comparisonAt = comparison
      ? repaymentAtDay(comparison, inspectDay)
      : null;
    return {
      day: inspectDay,
      date: toIsoDate(new Date(start.getTime() + inspectDay * DAY_MS)),
      primary: primaryAt,
      comparison: comparisonAt,
      // What the cap has held back so far: cash still in the business that
      // the uncapped schedule would already have collected.
      cashKept: comparisonAt
        ? Math.max(0, comparisonAt.paid - primaryAt.paid)
        : 0,
    };
  })();

  const payoffMarkers = [
    primary.complete && primary.days !== null && primary.days <= maxDay
      ? {
          day: primary.days,
          label: comparison ? t("legendWithCap") : t("legendPlan"),
          accent: true,
        }
      : null,
    comparison?.complete &&
    comparison.days !== null &&
    comparison.days <= maxDay
      ? { day: comparison.days, label: t("legendWithoutCap"), accent: false }
      : null,
  ].filter((marker): marker is NonNullable<typeof marker> => marker !== null);

  // Sit right of the guide line, flip left when that would overflow, and
  // clamp inside the chart so a narrow container never clips the tooltip.
  const guideX = inspect ? x(inspect.day) : 0;
  const tooltipLeft = (() => {
    const right = guideX + TOOLTIP_GAP;
    const left =
      right + tooltipWidth > width
        ? guideX - TOOLTIP_GAP - tooltipWidth
        : right;
    return Math.max(0, Math.min(left, width - tooltipWidth));
  })();

  React.useLayoutEffect(() => {
    if (tooltipRef.current) setTooltipWidth(tooltipRef.current.offsetWidth);
  }, [inspect?.day, inspect?.cashKept, width]);

  return (
    <Card
      className={cn("flex flex-col gap-5", className)}
      stack={stack}
      connectors={connectors}
      data-testid="pricing-chart"
    >
      {/* Legend sits on the title's baseline rather than floating at the top. */}
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-ds-heading-h5 text-grey-950">{t("title")}</h3>
          <p className="text-ds-body-sm text-grey-600">{t("description")}</p>
        </div>
        <ul className="text-ds-label-lg text-grey-600 flex items-center gap-4">
          <li className="flex items-center gap-2">
            <span
              aria-hidden
              className="bg-pulse-700 rounded-ds-full h-0.5 w-5"
            />
            {comparison ? t("legendWithCap") : t("legendPlan")}
          </li>
          {comparison && (
            <li className="flex items-center gap-2">
              <span
                aria-hidden
                className="border-grey-400 h-0 w-5 border-t-2 border-dashed"
              />
              {t("legendWithoutCap")}
            </li>
          )}
        </ul>
      </div>

      <div ref={containerRef} className="relative w-full select-none">
        {width > 0 && (
          <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            // A group, not an image: `img` would make the nested inspection
            // slider presentational and hide it from screen readers.
            role="group"
            aria-label={t("title")}
            className="block overflow-visible"
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor="var(--pulse-700)"
                  stopOpacity="0.14"
                />
                <stop
                  offset="100%"
                  stopColor="var(--pulse-700)"
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>

            {yTicks.map((tick) => (
              <g key={tick}>
                <line
                  x1={pad.left}
                  x2={width - pad.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke="var(--grey-100)"
                  strokeWidth={1}
                />
                <text
                  x={pad.left - 8}
                  y={y(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="var(--grey-500)"
                  fontSize={11}
                  className="tabular-nums"
                >
                  {format.compactMoney(tick)}
                </text>
              </g>
            ))}

            {xTicks.map((tick) => (
              <text
                key={tick.index}
                x={x(tick.day)}
                y={height - pad.bottom + 18}
                textAnchor="middle"
                fill="var(--grey-500)"
                fontSize={11}
                className="tabular-nums"
              >
                {tick.index}
              </text>
            ))}
            <text
              x={pad.left + plotWidth / 2}
              y={height - 4}
              textAnchor="middle"
              fill="var(--grey-400)"
              fontSize={11}
            >
              {t("xAxis")}
            </text>

            <path d={areaPath} fill={`url(#${gradientId})`} />
            {comparisonPath && (
              <path
                d={comparisonPath}
                fill="none"
                stroke="var(--grey-400)"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                strokeLinejoin="round"
              />
            )}
            <path
              d={primaryPath}
              fill="none"
              stroke="var(--pulse-700)"
              strokeWidth={2}
              strokeLinejoin="round"
            />

            {payoffMarkers.map((marker) => (
              <g key={marker.label}>
                <circle
                  cx={x(marker.day)}
                  cy={y(0)}
                  r={5}
                  fill={marker.accent ? "var(--pulse-700)" : "var(--grey-0)"}
                  stroke={marker.accent ? "var(--grey-0)" : "var(--grey-400)"}
                  strokeWidth={2}
                />
              </g>
            ))}

            {inspect && (
              <g>
                <line
                  x1={x(inspect.day)}
                  x2={x(inspect.day)}
                  y1={pad.top}
                  y2={y(0)}
                  stroke="var(--grey-300)"
                  strokeWidth={1}
                />
                {inspect.comparison && inspect.cashKept > 0 && (
                  <line
                    x1={x(inspect.day)}
                    x2={x(inspect.day)}
                    y1={y(inspect.comparison.remaining)}
                    y2={y(inspect.primary.remaining)}
                    stroke="var(--green-700)"
                    strokeWidth={2}
                  />
                )}
                {inspect.comparison && (
                  <circle
                    cx={x(inspect.day)}
                    cy={y(inspect.comparison.remaining)}
                    r={4}
                    fill="var(--grey-0)"
                    stroke="var(--grey-400)"
                    strokeWidth={1.5}
                  />
                )}
                <circle
                  cx={x(inspect.day)}
                  cy={y(inspect.primary.remaining)}
                  r={5}
                  fill="var(--pulse-700)"
                  stroke="var(--grey-0)"
                  strokeWidth={2}
                />
              </g>
            )}

            <rect
              x={pad.left}
              y={pad.top}
              width={plotWidth}
              height={plotHeight}
              fill="transparent"
              role="slider"
              tabIndex={0}
              aria-label={t("inspectLabel")}
              aria-valuemin={0}
              aria-valuemax={maxDay}
              aria-valuenow={inspect?.day ?? 0}
              aria-valuetext={
                inspect
                  ? [
                      `${format.date(inspect.date)}: ${format.money(inspect.primary.remaining)} ${t("tooltipRemaining").toLowerCase()}`,
                      inspect.cashKept > 0
                        ? `${t("tooltipCashKept")} ${format.money(inspect.cashKept)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : undefined
              }
              // No outline: the guide line and tooltip are the focus
              // indicator, shown on keyboard focus below.
              className="cursor-crosshair touch-none outline-none focus:outline-none"
              style={{ outline: "none" }}
              onPointerMove={onPointerMove}
              onPointerDown={onPointerDown}
              onPointerLeave={onPointerLeave}
              onKeyDown={onKeyDown}
              onFocus={(event) => {
                if (
                  inspectDay === null &&
                  event.currentTarget.matches(":focus-visible")
                ) {
                  setPinned(true);
                  setInspectDay(0);
                }
              }}
              onBlur={() => {
                if (!pinned) setInspectDay(null);
              }}
            />
          </svg>
        )}

        {inspect && width > 0 && (
          <div
            ref={tooltipRef}
            role="tooltip"
            className="border-grey-100 bg-grey-0 shadow-ds-sm text-ds-body-xs rounded-ds-l pointer-events-none absolute top-2 z-10 max-w-full min-w-[180px] border p-3 tabular-nums"
            style={{ left: tooltipLeft }}
          >
            <p className="text-grey-950 font-medium">
              {format.date(inspect.date)}{" "}
              <span className="text-grey-500 font-normal">
                · {t("tooltipDay", { day: inspect.day })}
              </span>
            </p>
            <dl className="mt-2 flex flex-col gap-1.5">
              <TooltipRow
                swatch="solid"
                label={comparison ? t("legendWithCap") : t("legendPlan")}
                remaining={format.money(inspect.primary.remaining)}
                repaid={format.money(inspect.primary.paid)}
                remainingLabel={t("tooltipRemaining")}
                repaidLabel={t("tooltipRepaid")}
              />
              {inspect.comparison && (
                <TooltipRow
                  swatch="dashed"
                  label={t("legendWithoutCap")}
                  remaining={format.money(inspect.comparison.remaining)}
                  repaid={format.money(inspect.comparison.paid)}
                  remainingLabel={t("tooltipRemaining")}
                  repaidLabel={t("tooltipRepaid")}
                />
              )}
            </dl>
            {inspect.cashKept > 0 && (
              <p className="border-grey-100 mt-2 flex justify-between gap-4 border-t pt-2 font-medium text-green-700">
                <span>{t("tooltipCashKept")}</span>
                <span>+{format.money(inspect.cashKept)}</span>
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function TooltipRow({
  swatch,
  label,
  remaining,
  repaid,
  remainingLabel,
  repaidLabel,
}: {
  swatch: "solid" | "dashed";
  label: string;
  remaining: string;
  repaid: string;
  remainingLabel: string;
  repaidLabel: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-grey-600 flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            "inline-block w-4",
            swatch === "solid"
              ? "bg-pulse-700 rounded-ds-full h-0.5"
              : "border-grey-400 h-0 border-t-2 border-dashed"
          )}
        />
        {label}
      </dt>
      <dd className="text-grey-950 flex justify-between gap-4 pl-6">
        <span className="text-grey-500">{remainingLabel}</span>
        <span className="font-medium">{remaining}</span>
      </dd>
      <dd className="text-grey-950 flex justify-between gap-4 pl-6">
        <span className="text-grey-500">{repaidLabel}</span>
        <span>{repaid}</span>
      </dd>
    </div>
  );
}
